import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Chip, CircularProgress, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { apiClient } from '../../publicApiClient';
import { normalizeStudentLessonCities } from '../../utils/tutorSessionType';

const PREDICTION_DEBOUNCE_MS = 250;

function cityLabelFromPrediction(prediction: google.maps.places.AutocompletePrediction): string {
  const main = prediction.structured_formatting?.main_text?.trim();
  if (main) return main;
  const firstSegment = prediction.description.split(',')[0]?.trim();
  return firstSegment || prediction.description.trim();
}

function mergeCityOptions(...lists: Array<string[] | null | undefined>): string[] {
  return normalizeStudentLessonCities(lists.flatMap((list) => list ?? []));
}

export interface StudentLessonCitiesAutocompleteProps {
  value: string[];
  onChange: (cities: string[]) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  label?: string;
  placeholder?: string;
}

const StudentLessonCitiesAutocomplete: React.FC<StudentLessonCitiesAutocompleteProps> = ({
  value,
  onChange,
  disabled = false,
  error = false,
  helperText,
  label,
  placeholder,
}) => {
  const { t } = useTranslation();
  const { isLoaded } = useGoogleMaps();
  const [catalogCities, setCatalogCities] = useState<string[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [predictions, setPredictions] = useState<string[]>([]);
  const [predictionsLoading, setPredictionsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    apiClient
      .getCities()
      .then((cities) => {
        if (!cancelled) {
          setCatalogCities(normalizeStudentLessonCities(cities));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCatalogCities(['Warszawa']);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const query = inputValue.trim();
    if (!query || !isLoaded || typeof google === 'undefined' || !google.maps?.places) {
      setPredictions([]);
      setPredictionsLoading(false);
      return;
    }

    let cancelled = false;
    setPredictionsLoading(true);
    const timer = window.setTimeout(() => {
      const service = new google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        {
          input: query,
          types: ['(cities)'],
          componentRestrictions: { country: 'pl' },
        },
        (results, status) => {
          if (cancelled) return;
          setPredictionsLoading(false);
          if (status !== google.maps.places.PlacesServiceStatus.OK || !results?.length) {
            setPredictions([]);
            return;
          }
          setPredictions(normalizeStudentLessonCities(results.map(cityLabelFromPrediction)));
        }
      );
    }, PREDICTION_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [inputValue, isLoaded]);

  const options = useMemo(
    () => mergeCityOptions(predictions, catalogCities, value),
    [predictions, catalogCities, value]
  );

  const loading = catalogLoading || predictionsLoading;

  return (
    <Autocomplete
      multiple
      freeSolo
      filterSelectedOptions
      options={options}
      value={value}
      inputValue={inputValue}
      onInputChange={(_, next, reason) => {
        if (reason === 'reset') return;
        setInputValue(next);
      }}
      onChange={(_, next) => {
        onChange(normalizeStudentLessonCities(next));
        setInputValue('');
      }}
      disabled={disabled}
      loading={loading}
      isOptionEqualToValue={(option, selected) =>
        option.toLocaleLowerCase() === selected.toLocaleLowerCase()
      }
      renderTags={(tags, getTagProps) =>
        tags.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} size="small" label={option} {...tagProps} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t('tutor.profileCompletion.studentLessonCities', 'Student-place cities')}
          placeholder={
            placeholder ??
            t('tutor.profileCompletion.studentLessonCitiesPlaceholder', 'Start typing a city…')
          }
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default StudentLessonCitiesAutocomplete;
